import { MAP_THEME } from "../lib/map-theme";

// PRD-001Y Wave C (Q7c) field-pin taxonomy, shared by the Property map and the Command Center
// map so the two surfaces cannot drift. Both maps had the same blind spot: their pin reads were
// route-rooted, so a pin planted in the field and promoted at the desk stayed invisible until
// someone attached it to a route. The classifier below is the single place that decides which
// bucket a pin belongs to, and the style table is the single place that decides how it looks.
//
// Deliberately NOT in this module: anything that could carry a secret. Feature properties are
// limited to an id, a display name, the layer key, and resolved colours. Token ids, token
// hashes, human codes, scan payloads, guard ids, and device ids have no representation here and
// no reader that would supply them.

/** The three field-pin buckets. Ordered least-to-most settled for legend and layer stacking. */
export type FieldPinLayer = "draft" | "unrouted_confirmed" | "routed_confirmed";

export const FIELD_PIN_LAYERS: readonly FieldPinLayer[] = [
  "draft",
  "unrouted_confirmed",
  "routed_confirmed",
] as const;

export interface FieldPinLayerStyle {
  /** Layer key, also the value of the `pinLayer` feature property used by MapLibre filters. */
  layer: FieldPinLayer;
  /** Toggle and legend label. */
  label: string;
  /** One-line operator explanation; the maps use it as toggle help text. */
  hint: string;
  color: string;
  strokeColor: string;
}

const STYLES: Record<FieldPinLayer, FieldPinLayerStyle> = {
  draft: {
    layer: "draft",
    label: "Field drafts",
    hint: "Provisional pins from a Setup walk, not confirmed yet.",
    color: MAP_THEME.fieldPin.draft,
    strokeColor: MAP_THEME.fieldPin.draftStroke,
  },
  unrouted_confirmed: {
    layer: "unrouted_confirmed",
    label: "Unrouted pins",
    hint: "Confirmed placements that are not on a patrol route yet.",
    color: MAP_THEME.fieldPin.unroutedConfirmed,
    strokeColor: MAP_THEME.fieldPin.defaultStroke,
  },
  routed_confirmed: {
    layer: "routed_confirmed",
    label: "Routed checkpoints",
    hint: "Confirmed stops on a patrol route.",
    color: MAP_THEME.fieldPin.routedConfirmed,
    strokeColor: MAP_THEME.fieldPin.defaultStroke,
  },
};

export function fieldPinLayerStyle(layer: FieldPinLayer): FieldPinLayerStyle {
  return STYLES[layer];
}

/**
 * Fill colour for a pin, honouring BOTH axes.
 *
 * Placement bucket and operational status are independent, and CAD coloured by status long
 * before Wave C existed: a checkpoint in maintenance drew amber and an archived one drew grey.
 * Colouring purely by bucket would have quietly promoted both to the settled green reserved for
 * healthy live patrol stops, which is a worse lie than the blind spot Wave C set out to fix.
 * So a non-active status wins the fill, and the bucket still owns the ring plus the layer,
 * toggle, and popup label, which is where the taxonomy signal actually needs to live.
 *
 * Read directly from MAP_THEME rather than through checkpointColor to keep this module free of
 * a cycle back into map-points.
 */
export function fieldPinFillColor(layer: FieldPinLayer, verificationStatus: string): string {
  if (isNonActiveStatus(verificationStatus)) {
    const statusColors: Record<string, string> = MAP_THEME.checkpointStatus;
    return statusColors[verificationStatus] ?? MAP_THEME.checkpointStatus.default;
  }
  return STYLES[layer].color;
}

function isNonActiveStatus(verificationStatus: string): boolean {
  return verificationStatus !== "" && verificationStatus !== "active";
}

/**
 * Ring colour. Normally the bucket's own stroke (white, or violet for a draft).
 *
 * When a non-active status has taken over the fill, the ring switches to the BUCKET colour
 * instead. Without that, a routed maintenance pin and an unrouted maintenance pin would be
 * pixel-identical: same amber fill, same white ring. The taxonomy would survive only in the
 * toggle and the popup, and a map you have to click to read is not a map. This way the fill
 * always answers "is this checkpoint healthy" and the ring always answers "where does it sit in
 * the setup lifecycle", on every pin, at a glance.
 */
export function fieldPinStrokeColor(layer: FieldPinLayer, verificationStatus: string): string {
  if (isNonActiveStatus(verificationStatus)) {
    return STYLES[layer].color;
  }
  return STYLES[layer].strokeColor;
}

/**
 * Which bucket a pin belongs to.
 *
 * Provisional wins over route membership on purpose. The DB refuses to attach a provisional pin
 * to a route (add_checkpoint_to_route, 20260801120000), so `provisional AND routed` is not a
 * reachable state; if bad data ever produces one, calling it a draft is the honest read and
 * keeps it out of the layer operators trust for live patrol work.
 */
export function classifyFieldPin(input: {
  placementState: string;
  isRouted: boolean;
}): FieldPinLayer {
  if (input.placementState === "provisional") {
    return "draft";
  }
  return input.isRouted ? "routed_confirmed" : "unrouted_confirmed";
}

/** A pin reduced to exactly what a map needs. No provenance beyond the bucket, no secrets. */
export interface FieldPin {
  checkpointId: string;
  name: string;
  lat: number;
  lng: number;
  layer: FieldPinLayer;
  siteName: string | null;
  routeName: string | null;
  /**
   * Operational status, orthogonal to the placement bucket. Omit for readers that only surface
   * active pins (the placement reader filters active = true), which is treated as "active".
   */
  verificationStatus?: string;
}

export interface FieldPinFeature {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: Record<string, string>;
}

export interface FieldPinFeatureCollection {
  type: "FeatureCollection";
  features: FieldPinFeature[];
}

/**
 * GeoJSON for the field-pin source. One source feeds all three layers; each layer filters on
 * `pinLayer`, so toggling one bucket never re-uploads the others' geometry.
 */
export function toFieldPinFeatureCollection(
  pins: readonly FieldPin[],
): FieldPinFeatureCollection {
  return {
    type: "FeatureCollection",
    features: pins.map((pin) => {
      const style = fieldPinLayerStyle(pin.layer);
      const status = pin.verificationStatus ?? "active";
      return {
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [pin.lng, pin.lat] },
        properties: {
          id: pin.checkpointId,
          name: pin.name,
          pinLayer: pin.layer,
          // Human-readable bucket for the hover popup, so an operator does not have to decode
          // the colour to learn that a pin is a draft rather than a live patrol stop.
          layerLabel: style.label,
          color: fieldPinFillColor(pin.layer, status),
          strokeColor: fieldPinStrokeColor(pin.layer, status),
          status,
          site: pin.siteName ?? "",
          route: pin.routeName ?? "",
        },
      };
    }),
  };
}

/** Count per bucket, for the legend and the "nothing to show" copy. */
export function countFieldPinLayers(
  pins: readonly FieldPin[],
): Record<FieldPinLayer, number> {
  const counts: Record<FieldPinLayer, number> = {
    draft: 0,
    unrouted_confirmed: 0,
    routed_confirmed: 0,
  };
  for (const pin of pins) {
    counts[pin.layer] += 1;
  }
  return counts;
}

/** Plural-safe summary for a toggle label, e.g. "Field drafts (2)". */
export function fieldPinLayerLabelWithCount(layer: FieldPinLayer, count: number): string {
  return `${fieldPinLayerStyle(layer).label} (${count})`;
}
